import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import type {
  ChartOfAccount,
  PettyCashClose,
  PettyCashCount,
  PettyCashEntry,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  ApprovalStatusBadge,
  PCRealizeVatAction,
  ReceiptBadge,
  ReceiptViewLink,
  SEARCH_BG,
  isEngineManaged,
  type Branch,
  type EntryWithApproval,
} from './pc-helpers';
import { PCClosesSection } from './PCClosesSection';
import { PCCountsSection } from './PCCountsSection';
import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';
import { buildCashBook, isCashAffecting, type CashBookRow } from '../../routes/pettycash.logic';
import { Can } from '../Can';

interface PCEntriesProps {
  branch: Branch;
  allBranches: readonly Branch[];
  setBranch: (b: Branch) => void;
  entries: readonly PettyCashEntry[];
  coa: ChartOfAccount[];
  companyName: string | undefined;
  period: string;
  periodLabel: string;
  isPending: boolean;
  onAdd: () => void;
  onImport: () => void;
  onOpenApproval: (entry: EntryWithApproval) => void;
  onCount: () => void;
  onClose: () => void;
  counts: readonly PettyCashCount[];
  closes: readonly PettyCashClose[];
  onOpenCountApproval: (count: PettyCashCount) => void;
  onOpenCloseApproval: (close: PettyCashClose) => void;
  companyId: string | undefined;
}

// 'YYYY-MM-DD' for a (possibly Date) entryDate — entries arrive ISO-stringified.
function entryIso(d: PettyCashEntry['entryDate']): string {
  return new Date(d).toISOString().slice(0, 10);
}

export function PCEntries({
  branch,
  allBranches,
  setBranch,
  entries,
  coa,
  companyName,
  period,
  periodLabel,
  isPending,
  onAdd,
  onImport,
  onOpenApproval,
  onCount,
  onClose,
  counts,
  closes,
  onOpenCountApproval,
  onOpenCloseApproval,
  companyId,
}: PCEntriesProps) {
  const [q, setQ] = useState<string>('');
  const [receiptFilter, setReceiptFilter] = useState<'all' | 'with' | 'without'>('all');
  // Category code → Thai name, from the real COA (postable expense/cost accounts).
  const coaName = useMemo(() => new Map(coa.map((a) => [a.code, a.nameTh])), [coa]);

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (
          q &&
          !e.description.toLowerCase().includes(q.toLowerCase()) &&
          !e.voucherNo.toLowerCase().includes(q.toLowerCase())
        )
          return false;
        if (receiptFilter === 'with' && !e.hasReceipt) return false;
        if (receiptFilter === 'without' && e.hasReceipt) return false;
        return true;
      }),
    [entries, q, receiptFilter],
  );

  // Real running cash-book: opening = float amount, descending (newest first).
  // The FULL filtered list (incl. PENDING_APPROVAL / REJECTED) still renders in
  // the table — buildCashBook only moves the running balance on cash-affecting
  // rows, so the carry-forward bal stays in sync with float.currentBalance.
  const withBal: CashBookRow[] = useMemo(
    () => buildCashBook(filtered, branch.float),
    [filtered, branch.float],
  );
  // Spend metrics (totalSpent / byDay chart) count ONLY cash-affecting rows —
  // PENDING_APPROVAL (deferred JV, no cash moved) and REJECTED (inert) rows are
  // excluded so the totals reconcile to the authoritative float balance.
  const cashEntries = useMemo(() => filtered.filter((e) => isCashAffecting(e)), [filtered]);
  const totalSpent = useMemo(
    () => cashEntries.reduce((a, e) => a + Number(e.amount), 0),
    [cashEntries],
  );

  // Daily spend bars derived from the real entries' month. We span the
  // calendar month the entries fall in (falls back to the SELECTED period when
  // empty) so the chart stays close to the prototype's day-bar look but real.
  const byDay = useMemo(() => {
    const m: Record<string, number> = {};
    cashEntries.forEach((e) => {
      const d = entryIso(e.entryDate);
      m[d] = (m[d] ?? 0) + Number(e.amount);
    });
    return m;
  }, [cashEntries]);
  const chartMonth = useMemo(() => {
    const first = Object.keys(byDay).sort()[0];
    return first ? first.slice(0, 7) : period; // 'YYYY-MM'
  }, [byDay, period]);
  const daysInMonth = useMemo(() => {
    const parts = chartMonth.split('-');
    return new Date(Number(parts[0]), Number(parts[1]), 0).getDate();
  }, [chartMonth]);
  const dayMax = Math.max(...Object.values(byDay), 1);

  async function handleExportEntries() {
    const cols: ExportColumn<CashBookRow>[] = [
      { header: 'voucherNo', value: (r) => r.voucherNo, width: 14 },
      { header: 'entryDate', value: (r) => entryIso(r.entryDate), width: 12 },
      { header: 'categoryCode', value: (r) => r.categoryCode, width: 12 },
      { header: 'description', value: (r) => r.description, width: 36 },
      { header: 'amount', value: (r) => Number(r.amount), width: 14 },
      { header: 'bal', value: (r) => r.bal, width: 14 },
    ];
    await exportRows(
      `เงินสดย่อย_${branch.id}_${companyName ?? 'COMPANY'}_${todayStamp()}.xlsx`,
      'สมุดเงินสดย่อย',
      cols,
      withBal,
    );
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div
          style={{
            padding: '16px 18px',
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
            gap: 20,
            alignItems: 'center',
          }}
        >
          <div>
            <div className="faint" style={{ fontSize: 14 }}>
              สาขาที่เลือก
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <select
                className="select"
                style={{ width: 280, height: 36, fontSize: 15 }}
                value={branch.id}
                onChange={(e) => {
                  const next = allBranches.find((b) => b.id === e.target.value);
                  if (next) setBranch(next);
                }}
              >
                {allBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.id} — {b.name}
                  </option>
                ))}
              </select>
              <span className="badge brand">งวด {periodLabel}</span>
            </div>
            <div className="faint" style={{ fontSize: 14, marginTop: 6 }}>
              ผู้รับผิดชอบ: <b style={{ color: '#3f3f46' }}>{branch.custodian}</b> ·
              เลขที่เงินสดย่อย {branch.id}-PC
            </div>
          </div>
          <div>
            <div className="faint" style={{ fontSize: 14 }}>
              วงเงินอนุมัติ
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                marginTop: 4,
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(branch.float)}
            </div>
            <div className="faint" style={{ fontSize: 14 }}>
              บาท
            </div>
          </div>
          <div>
            <div className="faint" style={{ fontSize: 14 }}>
              ใช้ไป ({periodLabel})
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                marginTop: 4,
                color: 'var(--brand)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(branch.used)}
            </div>
            <div className="faint" style={{ fontSize: 14 }}>
              {branch.usedPct}% ของวงเงิน
            </div>
          </div>
          <div>
            <div className="faint" style={{ fontSize: 14 }}>
              เงินสดคงเหลือ
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                marginTop: 4,
                color: 'var(--positive)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(branch.remain)}
            </div>
            <div className="faint" style={{ fontSize: 14 }}>
              บาท
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button className="btn primary" onClick={onAdd}>
              <Icon name="plus" size={14} /> บันทึกรายการใหม่
            </button>
            <button className="btn" onClick={onImport}>
              <Icon name="upload" size={14} /> นำเข้าจาก Excel
            </button>
            <Can permission="pettycash.count.create">
              <button
                className="btn"
                data-testid="pc-count-open"
                onClick={onCount}
                disabled={branch.status === 'reimbursed'}
                title="นับเงินสดจริงในมือ แล้วเทียบกับยอดในบัญชี"
              >
                <Icon name="check" size={14} /> นับเงินสด
              </button>
            </Can>
            <Can permission="pettycash.close.create">
              <button
                className="btn"
                data-testid="pc-close-open"
                onClick={onClose}
                disabled={branch.status === 'reimbursed'}
                title="ปิด float — คืนเงินสดคงเหลือเข้าบัญชีธนาคาร"
              >
                <Icon name="x" size={14} /> ปิด float
              </button>
            </Can>
          </div>
        </div>
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '12px 18px',
            background: '#fafafa',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <div className="meta">การใช้จ่ายรายวัน — {fmtDate(`${chartMonth}-01`).slice(3)}</div>
            <div className="meta">
              รวม {fmt(totalSpent)} บาท · {cashEntries.length} รายการ
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 48 }}>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const d = `${chartMonth}-${String(i + 1).padStart(2, '0')}`;
              const v = byDay[d] ?? 0;
              return (
                <div
                  key={i}
                  title={v ? `${fmtDate(d)} · ${fmt(v)} บาท` : undefined}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${(v / dayMax) * 100}%`,
                      background: v ? 'var(--brand)' : '#e5e5e9',
                      borderRadius: '2px 2px 0 0',
                      minHeight: v ? 2 : 0,
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span className="meta">{fmtDate(`${chartMonth}-01`)}</span>
            <span className="meta">
              {fmtDate(`${chartMonth}-${String(daysInMonth).padStart(2, '0')}`)}
            </span>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา เลขที่ / รายการ"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 32,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '10px center',
            }}
          />
          <select
            className="select"
            value={receiptFilter}
            onChange={(e) => setReceiptFilter(e.target.value as 'all' | 'with' | 'without')}
          >
            <option value="all">ใบเสร็จ: ทั้งหมด</option>
            <option value="with">มีใบเสร็จ</option>
            <option value="without">ไม่มีใบเสร็จ</option>
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <span className="meta">แสดง {withBal.length} รายการ</span>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 480 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input type="checkbox" />
                </th>
                <th style={{ width: 110 }}>เลขที่</th>
                <th style={{ width: 95 }}>วันที่</th>
                <th>ประเภทค่าใช้จ่าย</th>
                <th>รายละเอียด</th>
                <th className="num">จำนวนเงิน</th>
                <th>สถานะ</th>
                <th className="num">คงเหลือ</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : withBal.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่มีรายการเงินสดย่อยของสาขานี้ในงวดที่เลือก
                  </td>
                </tr>
              ) : null}
              {withBal.map((e) => (
                <tr key={e.id}>
                  <td>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{e.voucherNo}</a>
                  </td>
                  <td>{fmtDate(entryIso(e.entryDate))}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: 'var(--text-faint)',
                        }}
                      />
                      <span className="code muted" style={{ fontSize: 14 }}>
                        {e.categoryCode}
                      </span>
                      <span>{coaName.get(e.categoryCode) ?? '—'}</span>
                    </span>
                  </td>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 4,
                      }}
                    >
                      {e.description}
                      {e.hasReceipt && <ReceiptViewLink companyId={companyId} entryId={e.id} />}
                    </span>
                  </td>
                  <td className="num tnum">{fmt(Number(e.amount))}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 4,
                      }}
                    >
                      {e.status === 'PENDING_APPROVAL' ? (
                        <ApprovalStatusBadge approvalStatus={e.approvalStatus} />
                      ) : e.status === 'REJECTED' ? (
                        <span className="badge negative">
                          <span className="bdot" />
                          ไม่อนุมัติ
                        </span>
                      ) : e.status === 'REPLENISHED' ? (
                        <span className="badge info">
                          <span className="bdot" />
                          ชดเชยแล้ว
                        </span>
                      ) : (
                        <span className="badge positive">
                          <span className="bdot" />
                          ลงบัญชีแล้ว
                        </span>
                      )}
                      {isEngineManaged(e) && (
                        <button
                          type="button"
                          className="btn sm ghost"
                          data-testid={`pc-open-approval-${e.id}`}
                          style={{ fontSize: 12, height: 22, padding: '0 6px' }}
                          onClick={() => onOpenApproval(e)}
                          title="ดูสายอนุมัติ"
                        >
                          สายอนุมัติ
                        </button>
                      )}
                      {e.vatTreatment === 'SUSPENSE' && !e.vatRealizedAt && (
                        <span className="badge warning" style={{ fontSize: 12 }}>
                          <span className="bdot" />
                          ภาษีซื้อค้างรับ
                        </span>
                      )}
                      {e.vatTreatment === 'SUSPENSE' && e.vatRealizedAt && (
                        <span className="badge positive" style={{ fontSize: 12 }}>
                          <span className="bdot" />
                          รับรู้ภาษีแล้ว
                        </span>
                      )}
                      <PCRealizeVatAction companyId={companyId} entry={e} />
                      <ReceiptBadge
                        hasReceipt={e.hasReceipt ?? false}
                        isNoDoc={e.isNoDoc ?? false}
                      />
                    </span>
                  </td>
                  <td className="num tnum muted">{fmt(e.bal)}</td>
                </tr>
              ))}
              {withBal.length > 0 && (
                <tr className="total">
                  <td colSpan={5}>รวมเงินสดย่อยที่จ่าย {cashEntries.length} รายการ</td>
                  <td className="num tnum">{fmt(totalSpent)}</td>
                  <td />
                  <td className="num tnum">{fmt(branch.remain)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            แสดง {withBal.length} รายการ · ยอดยกมาต้นเดือน: {fmt(branch.float)} บาท · คงเหลือ:{' '}
            <b>{fmt(branch.remain)}</b> บาท
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => void handleExportEntries()}
              disabled={withBal.length === 0}
            >
              <Icon name="download" size={12} /> ส่งออก Excel
            </button>
            <button className="btn sm primary" disabled title="เร็วๆ นี้">
              <Icon name="check" size={12} /> ส่งให้ HQ อนุมัติ
            </button>
          </div>
        </div>
      </div>

      <PCCountsSection counts={counts} onOpenApproval={onOpenCountApproval} />
      <PCClosesSection closes={closes} onOpenApproval={onOpenCloseApproval} />
    </>
  );
}

export type { PCEntriesProps };
